import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion, useInView, useMotionValue, useTransform, animate } from 'framer-motion';
import Marquee from 'react-fast-marquee';
import { FaGoogle, FaAmazon, FaMeta, FaApple, FaMicrosoft, FaSpotify, FaLinkedin, FaSlack } from 'react-icons/fa6';
import { track, EVENTS } from '../utils/analytics';
import { captureAttributionFromUrl } from '../utils/attribution';
import axios from 'axios';
import {
  ArrowRight, Check, Upload, Heart, Target, Wand2, Eye, ExternalLink,
  ListChecks, ChevronDown, Menu, X, Shield, FileText, Sparkles, Search, Star,
} from 'lucide-react';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ── Central copy for the v1 positioning ── */
export const PRODUCT_COPY = {
  headline: 'Find Better Jobs. Tailor Every Application with AI.',
  subheadline: 'Discover relevant opportunities, tailor your CV and cover letter for each role, and manage your job search from one place.',
  promise: 'Find relevant jobs. Tailor every application with AI. Apply more effectively.',
  trial: '7-day free trial · 25 AI tailorings · No card required',
  flow: ['Discover', 'Swipe', 'Match', 'Tailor', 'Review', 'Apply', 'Track'],
};

const STEPS = [
  { icon: Upload, title: 'Upload your CV', desc: 'JobSurfAI understands your experience, skills and background in seconds.' },
  { icon: Heart, title: 'Discover & swipe', desc: 'Swipe through relevant jobs. Left = not interested. Right = interested.' },
  { icon: Target, title: 'Match', desc: 'See how well your profile fits each opportunity, and what is missing.' },
  { icon: Wand2, title: 'Tailor', desc: 'AI tailors your CV and writes a job-specific cover letter for that role.' },
  { icon: Eye, title: 'Review', desc: 'Read, edit and download your tailored materials. You stay in control.' },
  { icon: ExternalLink, title: 'Apply', desc: "We send you to the employer's original application page to apply." },
  { icon: ListChecks, title: 'Track', desc: 'Track every application, interview and offer inside JobSurfAI.' },
];

const FEATURES = [
  { icon: Search, title: 'Job discovery', desc: 'Fresh roles from 20+ job sources, matched to your profile and refreshed daily.' },
  { icon: Heart, title: 'Swipe feed', desc: 'Think Tinder, but for finding your next job. Swipe right to save roles you like.' },
  { icon: Target, title: 'AI job matching', desc: 'A clear match score for every role, with the skills that matter highlighted.' },
  { icon: FileText, title: 'Tailored CV per job', desc: 'Create a job-specific CV version, review the changes and download it.' },
  { icon: Sparkles, title: 'AI cover letters', desc: 'Generate a cover letter tailored to the role — then edit it your way.' },
  { icon: ListChecks, title: 'Application tracking', desc: 'From "interested" to "offer": every application, one dashboard.' },
];

const FAQS = [
  { q: 'What is JobSurfAI?', a: 'An AI-powered job discovery and application tailoring platform. It finds relevant jobs, matches them to your CV, tailors your CV and cover letter for each role, and tracks your applications.' },
  { q: 'How does JobSurfAI find jobs?', a: 'We pull fresh listings from 20+ job boards and company career pages, then rank them against your CV, preferred roles and locations.' },
  { q: 'Does JobSurfAI apply to jobs for me?', a: "JobSurfAI helps you discover relevant opportunities and prepare tailored application materials. In the current version, you review your materials and complete the final application on the employer's website." },
  { q: 'How does AI tailoring work?', a: 'We analyse the job description, compare it with your CV, rewrite and reorder your CV to fit the role, and generate a matching cover letter. You review and edit everything before you use it.' },
  { q: 'What counts as one AI tailoring?', a: 'Preparing a tailored application for one specific job — job analysis, CV tailoring and cover letter together. Editing or re-downloading materials for the same job is free. Submitting the application is never counted.' },
  { q: 'How long is the free trial?', a: '7 days, with 25 AI tailorings included. No card required.' },
  { q: 'What happens after my trial?', a: 'You pick a monthly plan to continue. Your CVs, saved jobs and application history stay exactly where they are.' },
  { q: 'Can I cancel?', a: 'Yes, anytime from your subscription page. You keep access until the end of the billing period and are not charged again.' },
];

const TESTIMONIALS = [
  { name: 'Sarah J.', role: 'Product Manager', company: 'Series B startup', avatar: 'https://images.unsplash.com/photo-1672685667592-0392f458f46f?w=200&q=80',
    quote: "Saw a TikTok and was sure it was a scam. Three weeks later I've had 4 first-round interviews. The tailored cover letters are scary good." },
  { name: 'David L.', role: 'Frontend Engineer', company: 'Berlin', avatar: 'https://images.unsplash.com/photo-1576558656222-ba66febe3dec?w=200&q=80',
    quote: 'Was applying manually for months and barely getting replies. With JobSurf I tailored 60+ applications in a week and landed 3 interviews.' },
  { name: 'Elena R.', role: 'Marketing Lead', company: 'Madrid', avatar: 'https://images.unsplash.com/photo-1655249481446-25d575f1c054?w=200&q=80',
    quote: 'Job hunting while employed is a nightmare. JobSurf finds the roles and preps my CV and cover letter — I just review and hit apply. Cancelled my LinkedIn Premium.' },
  { name: 'Marcus T.', role: 'Data Analyst', company: 'London', avatar: 'https://images.unsplash.com/photo-1539571696357-5a69c17a67c6?w=200&q=80',
    quote: 'The AI tailors every CV and cover letter to the job. I read them and they sound like me — but better. Saving me 8-10 hours a week easily.' },
];

const STATS = [
  { v: 2400, suf: '+', label: 'Tailored applications created' },
  { v: 310, suf: '+', label: 'Interviews landed by users' },
  { v: 10, suf: '+ hrs', label: 'Saved per week' },
];

const StatNumber = ({ value, suffix = '+', duration = 1.8 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-50px' });
  const mv = useMotionValue(0);
  const display = useTransform(mv, (v) => Math.floor(v).toLocaleString());
  useEffect(() => { if (inView) animate(mv, value, { duration, ease: 'easeOut' }); }, [inView, value, duration, mv]);
  return <span ref={ref} className="tabular-nums"><motion.span>{display}</motion.span>{suffix}</span>;
};

const Reveal = ({ children, delay = 0, className = '' }) => (
  <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-60px' }}
    transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }} className={className}>
    {children}
  </motion.div>
);

const SectionLabel = ({ children }) => (
  <p className="text-[11px] sm:text-xs font-bold tracking-[0.2em] uppercase text-blue-400 mb-4">{children}</p>
);

/* ── Illustrative product demo (clearly labelled, not real statistics) ── */
const DemoMockup = () => (
  <div className="relative w-full max-w-[520px] mx-auto" data-testid="demo-mockup">
    <div className="absolute -inset-6 bg-blue-500/10 blur-3xl rounded-full" />
    <div className="relative rounded-2xl border border-white/10 bg-[#0b0f17] p-5 shadow-2xl">
      <div className="flex items-center justify-between mb-4">
        <span className="text-[10px] uppercase tracking-widest text-zinc-500">Illustrative demo</span>
        <span className="text-[10px] text-emerald-400 font-semibold">Match 91%</span>
      </div>
      <div className="rounded-xl bg-white/[0.04] border border-white/[0.06] p-4 mb-3">
        <p className="text-sm font-bold text-white">Product Designer</p>
        <p className="text-xs text-zinc-400">Example Company · Amsterdam · Hybrid</p>
        <div className="flex gap-1.5 mt-3 flex-wrap">
          {['Figma', 'Design systems', 'User research'].map(t => (
            <span key={t} className="text-[10px] px-2 py-0.5 rounded-full bg-blue-500/15 text-blue-300 border border-blue-500/20">{t}</span>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 text-xs">
        {['Tailor CV', 'Cover letter', 'Review', 'Apply on company site'].map((a, i) => (
          <div key={a} className={`rounded-lg px-3 py-2 border ${i === 3 ? 'bg-blue-600 border-blue-500 text-white font-semibold' : 'bg-white/[0.03] border-white/[0.06] text-zinc-300'}`}>{a}</div>
        ))}
      </div>
      <div className="flex justify-between mt-4 text-[11px] text-zinc-500">
        <span>← Not interested</span><span>Interested →</span>
      </div>
    </div>
  </div>
);

const PlanCard = ({ id, plan, highlight, onSelect }) => (
  <div className={`relative rounded-2xl p-6 border flex flex-col ${highlight ? 'border-blue-500/60 bg-blue-500/[0.07] shadow-[0_0_40px_rgba(59,130,246,0.15)]' : 'border-white/[0.08] bg-white/[0.02]'}`} data-testid={`pricing-card-${id}`}>
    {highlight && <span className="absolute -top-3 left-6 text-[10px] font-bold uppercase tracking-wider bg-blue-500 text-white px-2.5 py-1 rounded-full">Most popular</span>}
    <p className="text-sm font-semibold text-zinc-300">{plan.name}</p>
    <p className="mt-2 text-3xl font-extrabold text-white">{plan.price === 0 ? 'Free' : `€${plan.price}`}<span className="text-sm font-medium text-zinc-500">{plan.price === 0 ? ' · 7 days' : '/month'}</span></p>
    <p className="text-xs text-zinc-500 mt-1 min-h-[2.5rem]">{plan.description}</p>
    <ul className="mt-4 space-y-2 flex-1">
      {plan.features.map(f => (
        <li key={f} className="flex items-start gap-2 text-sm text-zinc-300"><Check className="w-4 h-4 text-blue-400 mt-0.5 shrink-0" />{f}</li>
      ))}
    </ul>
    <button onClick={() => onSelect(id)} className={`mt-6 w-full rounded-full py-2.5 text-sm font-bold transition-all hover:scale-[1.02] ${highlight ? 'bg-blue-500 hover:bg-blue-400 text-white' : 'bg-white text-black hover:bg-zinc-200'}`} data-testid={`pricing-cta-${id}`}>
      {plan.price === 0 ? 'Start Free' : `Start free, then ${plan.name}`}
    </button>
  </div>
);

export default function LandingPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [plans, setPlans] = useState(null);
  const [openFaq, setOpenFaq] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    captureAttributionFromUrl();
    track(EVENTS.LANDING_VIEWED || 'landing_viewed', { variant: 'v1' });
    const params = new URLSearchParams(window.location.search);
    axios.post(`${API_BASE}/analytics/track-visit`, {
      path: window.location.pathname || '/', referrer: document.referrer || '',
      utm_source: params.get('utm_source') || '', utm_medium: params.get('utm_medium') || '', utm_campaign: params.get('utm_campaign') || '',
      utm_term: params.get('utm_term') || '', utm_content: params.get('utm_content') || '',
      ttclid: params.get('ttclid') || '', fbclid: params.get('fbclid') || '', gclid: params.get('gclid') || '',
    }).catch(() => {});
    axios.get(`${API_BASE}/subscription/plans`).then(r => setPlans(r.data)).catch(() => setPlans({}));
  }, []);

  const goSignup = (plan) => {
    track(EVENTS.CTA_CLICKED || 'cta_clicked', { plan: plan || 'trial' });
    navigate(isAuthenticated ? '/dashboard' : `/auth?mode=signup${plan && plan !== 'trial' ? `&plan=${plan}` : ''}`);
  };
  const scrollTo = (id) => { setMenuOpen(false); document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' }); };

  return (
    <div className="min-h-screen bg-[#050505] text-white font-[Manrope,sans-serif] antialiased selection:bg-blue-500/30" data-testid="landing-page">
      {/* Nav */}
      <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-[#050505]/70 border-b border-white/[0.06]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <button onClick={() => scrollTo('top')} className="flex items-center gap-2" data-testid="nav-logo">
            <span className="w-8 h-8 rounded-lg bg-blue-500 flex items-center justify-center font-black text-sm">JS</span>
            <span className="font-extrabold tracking-tight">JobSurf<span className="text-blue-400">AI</span></span>
          </button>
          <nav className="hidden md:flex items-center gap-7 text-sm text-zinc-400">
            <button onClick={() => scrollTo('how')} className="hover:text-white" data-testid="nav-how">How it works</button>
            <button onClick={() => scrollTo('features')} className="hover:text-white" data-testid="nav-features">Features</button>
            <button onClick={() => scrollTo('pricing')} className="hover:text-white" data-testid="nav-pricing">Pricing</button>
            <button onClick={() => scrollTo('faq')} className="hover:text-white" data-testid="nav-faq">FAQ</button>
          </nav>
          <div className="flex items-center gap-2">
            {isAuthenticated ? (
              <button onClick={() => navigate('/dashboard')} className="text-sm font-semibold bg-white/[0.06] border border-white/[0.08] rounded-full px-4 py-2" data-testid="nav-dashboard">Dashboard</button>
            ) : (
              <>
                <button onClick={() => navigate('/auth')} className="hidden sm:block text-sm text-zinc-300 hover:text-white px-3 py-2" data-testid="nav-login">Log in</button>
                <button onClick={() => goSignup()} className="text-sm font-bold text-black bg-white hover:bg-zinc-200 rounded-full px-4 py-2 transition-all hover:scale-[1.03]" data-testid="nav-signup">Start Free</button>
              </>
            )}
            <button className="md:hidden p-1.5" onClick={() => setMenuOpen(v => !v)} aria-label="Menu" data-testid="nav-mobile-toggle">{menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}</button>
          </div>
        </div>
        {menuOpen && (
          <div className="md:hidden border-t border-white/[0.06] bg-[#050505] px-6 py-4 flex flex-col gap-3 text-zinc-300" data-testid="nav-mobile-menu">
            {[['how', 'How it works'], ['features', 'Features'], ['pricing', 'Pricing'], ['faq', 'FAQ']].map(([id, l]) => (
              <button key={id} onClick={() => scrollTo(id)} className="text-left py-2" data-testid={`nav-mobile-${id}`}>{l}</button>
            ))}
            {!isAuthenticated && <button onClick={() => navigate('/auth')} className="text-left py-2" data-testid="nav-mobile-login">Log in</button>}
          </div>
        )}
      </header>

      {/* Hero */}
      <section id="top" className="relative pt-32 pb-16 sm:pt-40 sm:pb-24 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-[-10%] left-1/2 -translate-x-1/2 w-[900px] h-[500px] bg-blue-600/20 blur-[140px] rounded-full" />
        </div>
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <Reveal>
              <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/[0.04] border border-white/[0.08] text-xs text-zinc-300 mb-6" data-testid="hero-trial-pill">
                <Shield className="w-3.5 h-3.5 text-blue-400" /> {PRODUCT_COPY.trial}
              </span>
            </Reveal>
            <Reveal delay={0.05}>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] mb-5" data-testid="hero-headline">
                Find Better Jobs.<br /><span className="text-blue-400">Tailor Every Application</span> with AI.
              </h1>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="text-base md:text-lg text-zinc-400 max-w-xl leading-relaxed mb-8" data-testid="hero-subhead">{PRODUCT_COPY.subheadline}</p>
            </Reveal>
            <Reveal delay={0.15} className="flex flex-wrap gap-3">
              <button onClick={() => goSignup()} className="inline-flex items-center gap-2 bg-blue-500 hover:bg-blue-400 text-white font-bold rounded-full px-6 py-3.5 transition-all hover:scale-[1.03] shadow-[0_0_30px_rgba(59,130,246,0.35)]" data-testid="hero-cta-primary">
                Start Free <ArrowRight className="w-4 h-4" />
              </button>
              <button onClick={() => scrollTo('how')} className="inline-flex items-center gap-2 border border-white/[0.12] hover:bg-white/[0.05] text-white font-semibold rounded-full px-6 py-3.5 transition-colors" data-testid="hero-cta-secondary">
                See How It Works
              </button>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-5 text-xs text-zinc-500" data-testid="hero-trust-row">You review every application and submit it yourself on the employer's site. Cancel anytime.</p>
            </Reveal>
            <Reveal delay={0.25} className="mt-6 flex items-center gap-3" data-testid="hero-social-proof">
              <div className="flex -space-x-2">
                {TESTIMONIALS.map((t) => <img key={t.name} src={t.avatar} alt="" loading="lazy" className="w-9 h-9 rounded-full border-2 border-[#050505] object-cover" />)}
                <div className="w-9 h-9 rounded-full bg-zinc-800 border-2 border-[#050505] flex items-center justify-center text-[10px] font-bold text-white">+2K</div>
              </div>
              <div className="flex flex-col">
                <div className="flex items-center gap-0.5">{[...Array(5)].map((_, i) => <Star key={i} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />)}</div>
                <p className="text-xs text-zinc-500"><strong className="text-zinc-300">2,000+</strong> job seekers use JobSurfAI</p>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.2}><DemoMockup /></Reveal>
        </div>
      </section>

      {/* Flow strip */}
      <section className="border-y border-white/[0.06] bg-[#080808] py-6" data-testid="flow-strip">
        <div className="max-w-6xl mx-auto px-4 flex flex-wrap items-center justify-center gap-x-3 gap-y-2 text-[11px] sm:text-xs font-bold tracking-[0.18em] uppercase text-zinc-400">
          {PRODUCT_COPY.flow.map((s, i) => (
            <span key={s} className="flex items-center gap-3"><span className={i === 3 ? 'text-blue-400' : ''}>{s}</span>{i < PRODUCT_COPY.flow.length - 1 && <span className="text-zinc-700">→</span>}</span>
          ))}
        </div>
      </section>

      {/* Logos marquee */}
      <section className="py-10 border-b border-white/[0.06] bg-[#080808]" data-testid="logos-marquee">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <p className="text-center text-xs uppercase tracking-[0.2em] text-zinc-600 font-semibold mb-6">Users land interviews at</p>
          <Marquee gradient gradientColor="#080808" gradientWidth={60} speed={40} pauseOnHover>
            {[FaGoogle, FaAmazon, FaMeta, FaApple, FaMicrosoft, FaSpotify, FaLinkedin, FaSlack].map((Icon, i) => (
              <Icon key={i} className="w-7 h-7 sm:w-8 sm:h-8 text-zinc-600 hover:text-white mx-8 sm:mx-12 transition-colors" />
            ))}
          </Marquee>
        </div>
      </section>

      {/* Stats strip */}
      <section className="py-14 sm:py-20 border-b border-white/[0.06]" data-testid="stats-strip">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 grid grid-cols-3 gap-4 sm:gap-8">
          {STATS.map((st, i) => (
            <Reveal key={st.label} delay={i * 0.08} className="text-center">
              <p className="text-3xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight bg-gradient-to-b from-white to-zinc-400 bg-clip-text text-transparent"><StatNumber value={st.v} suffix={st.suf} /></p>
              <p className="text-xs sm:text-sm text-zinc-500 mt-1.5 sm:mt-2 font-medium">{st.label}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="py-20 sm:py-28">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <Reveal><SectionLabel>How it works</SectionLabel>
            <h2 className="text-base md:text-lg font-bold text-zinc-300 mb-2">From job discovery to a tailored application — all in one place.</h2>
            <p className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-12">Seven steps. You stay in control of every application.</p>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {STEPS.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.05}>
                <div className="h-full rounded-2xl border border-white/[0.08] bg-white/[0.02] p-5 hover:border-blue-500/40 transition-colors" data-testid={`step-${i + 1}`}>
                  <div className="flex items-center justify-between mb-4">
                    <span className="w-10 h-10 rounded-xl bg-blue-500/15 border border-blue-500/25 flex items-center justify-center"><s.icon className="w-5 h-5 text-blue-400" /></span>
                    <span className="text-xs font-bold text-zinc-600">0{i + 1}</span>
                  </div>
                  <h3 className="font-bold mb-1.5">{s.title}</h3>
                  <p className="text-sm text-zinc-400 leading-relaxed">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-20 sm:py-28 border-t border-white/[0.06] bg-[#070707]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <Reveal><SectionLabel>Features</SectionLabel>
            <p className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-3">Spend less time preparing applications.</p>
            <p className="text-zinc-400 max-w-2xl mb-12">And more time finding the right opportunities. Every plan includes all of this.</p>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {FEATURES.map((f, i) => (
              <Reveal key={f.title} delay={i * 0.04}>
                <div className="h-full rounded-2xl border border-white/[0.08] bg-white/[0.02] p-6" data-testid={`feature-${i + 1}`}>
                  <f.icon className="w-6 h-6 text-blue-400 mb-4" />
                  <h3 className="font-bold mb-1.5">{f.title}</h3>
                  <p className="text-sm text-zinc-400 leading-relaxed">{f.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="py-20 sm:py-28 border-t border-white/[0.06]" data-testid="testimonials-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <Reveal><SectionLabel>Real users, real interviews</SectionLabel>
            <p className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-12">"This AI got me interviews."</p>
          </Reveal>
          <div className="grid sm:grid-cols-2 gap-5">
            {TESTIMONIALS.map((t, i) => (
              <Reveal key={t.name} delay={i * 0.06}>
                <div className="bg-[#0E0E0E] border border-white/[0.08] rounded-2xl p-6 h-full hover:border-white/[0.15] transition-colors" data-testid={`testimonial-${i + 1}`}>
                  <div className="flex items-center gap-0.5 mb-3">{[...Array(5)].map((_, j) => <Star key={j} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />)}</div>
                  <p className="text-zinc-200 text-[15px] leading-relaxed mb-5">"{t.quote}"</p>
                  <div className="flex items-center gap-3">
                    <img src={t.avatar} alt="" loading="lazy" className="w-10 h-10 rounded-full object-cover border border-white/[0.08]" />
                    <div><p className="text-sm font-semibold text-white">{t.name}</p><p className="text-xs text-zinc-500">{t.role} · {t.company}</p></div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-20 sm:py-28 border-t border-white/[0.06]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <Reveal><SectionLabel>Pricing</SectionLabel>
            <p className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-3">Try JobSurfAI free for 7 days.</p>
            <p className="text-zinc-400 max-w-2xl mb-3">Then continue with a monthly plan. Plans differ by how many <span className="text-white font-semibold">AI tailorings</span> you get each month — one tailoring prepares your CV and cover letter for one job.</p>
            <p className="text-xs text-zinc-500 mb-12">7 days free. Continue with a paid plan afterward. Cancel anytime.</p>
          </Reveal>
          {plans === null ? (
            <div className="text-zinc-500 text-sm" data-testid="pricing-loading">Loading plans…</div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 pt-3" data-testid="pricing-grid">
              {['trial', 'basic', 'pro', 'power', 'unlimited'].filter(id => plans[id]).map(id => (
                <PlanCard key={id} id={id} plan={plans[id]} highlight={id === 'pro'} onSelect={goSignup} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-20 sm:py-28 border-t border-white/[0.06] bg-[#070707]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <Reveal><SectionLabel>FAQ</SectionLabel><p className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-10">Questions, answered.</p></Reveal>
          <div className="divide-y divide-white/[0.06]">
            {FAQS.map((f, i) => (
              <div key={f.q} data-testid={`faq-${i + 1}`}>
                <button onClick={() => setOpenFaq(openFaq === i ? -1 : i)} className="w-full flex items-center justify-between py-5 text-left font-semibold hover:text-blue-300 transition-colors" data-testid={`faq-toggle-${i + 1}`}>
                  {f.q}<ChevronDown className={`w-4 h-4 shrink-0 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === i && <p className="pb-5 text-sm text-zinc-400 leading-relaxed" data-testid={`faq-answer-${i + 1}`}>{f.a}</p>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 sm:py-28 border-t border-white/[0.06]">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <Reveal>
            <p className="text-3xl sm:text-5xl font-extrabold tracking-tight mb-4">Swipe. Match. Tailor. Apply.</p>
            <p className="text-zinc-400 mb-8">{PRODUCT_COPY.promise}</p>
            <button onClick={() => goSignup()} className="inline-flex items-center gap-2 bg-blue-500 hover:bg-blue-400 text-white font-bold rounded-full px-7 py-4 transition-all hover:scale-[1.03]" data-testid="final-cta">
              Start your 7-day free trial <ArrowRight className="w-4 h-4" />
            </button>
          </Reveal>
        </div>
      </section>

      <footer className="border-t border-white/[0.06] py-10 text-xs text-zinc-500">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p>© {new Date().getFullYear()} JobSurfAI — AI job search, CV tailoring and application tracking.</p>
          <div className="flex gap-5">
            <button onClick={() => navigate('/privacy')} className="hover:text-white" data-testid="footer-privacy">Privacy</button>
            <button onClick={() => navigate('/terms')} className="hover:text-white" data-testid="footer-terms">Terms</button>
            <a href="mailto:hello@jobsurfai.com" className="hover:text-white" data-testid="footer-contact">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
